import { BeatSequencer } from '@/story/sequencer';
import { LOADING_YARD_BEAT, YARD_CUES, YARD_FLAGS, YARD_IDS } from '@/story/act1/loadingYard';
import { BIN_ROW_Z, KESTNER_OBSERVE_AT, TRUCK_CAB_AT, isUnderTrailer } from '@/world/loadingYard';
import { bakeYardLightGrid, yardCover } from '@/world/loadingYardStealth';
import { YardDirector, type YardDebug } from '@/world/yardDirector';
import { StealthSystem, exposureAt, type LightGrid } from '@/systems/stealth';
import { FootfallStride, heardGait } from '@/systems/footfallStride';
import { emissionRadius } from '@/audio/sounds/foley';
import type { Interactable } from '@/systems/interaction';
import type { BeatPartCapabilities, BeatPartLoop, BeatPartModule, BeatPartModuleDefinition } from '@/game/beatPartModule';

/**
 * How much of the strike reaches the stealth light grid, and it is 1.0.
 *
 * `docs/beat-6-plan.md` rule 1: `sample(x, z) + storm.flash * FLASH_GAIN`,
 * clamped to 1. At 1.0 a peak strike takes **every** point in the yard to fully
 * lit, which is the mechanic stated exactly — *the whole world can see her*.
 *
 * Turning it down would be the wrong lever and this is the note that says so:
 * the flash is not supposed to be survivable in the open, it is supposed to be
 * survivable in **cover**, and cover is `exposureAt`'s other factor. Under the
 * trailer at concealment 0.75 a peak strike still lands at 0.25 exposure. The
 * number to move if the crossing plays wrong is the flash interval or the length
 * of Kestner's sweep.
 */
const FLASH_GAIN = 1;

/**
 * How hard the strike drives the sky term, D-107's second reader.
 *
 * The yard's authored `hemiIntensity` is 0.42 and its exposure reference is 1.0,
 * so at peak this puts the sky at 1.36 — key plus hemi plus ambient reaches 1.88
 * against a ramp anchored at 1.0, and every surface in the yard that is not in
 * a cast shadow clips to the top band. **That is the intent**: for one heartbeat
 * the haze reads through, the yard has a far side, and it is the only pure white
 * in the game (palette `accent`, `0xf2f6ff`).
 *
 * It is a multiplier on the room's own number rather than an absolute, so a
 * later tune of how dark the unlit yard is carries the flash with it.
 */
const FLASH_RIG_GAIN = 1;

/**
 * And how hard it lights the CLOUDS.
 *
 * `scene.background` is a baked equirectangular texture (D-106) and nothing was
 * touching it, so the first build of this beat put a white yard under a black
 * sky — which is exactly backwards. The thing a strike actually illuminates is
 * the cloud it is inside; the ground is lit second-hand. It cannot come from the
 * post stack, which MULTIPLIES the composited frame and cannot lift a night sky
 * that is already almost black (D-107).
 */
const FLASH_SKY_GAIN = 4;

/**
 * How much of her footfall survives the rain, 0..1.
 *
 * Spec §7.6: *"the storm covers her noise."* Beat 6 is the first and only place
 * in the game where the player's own footsteps stop mattering much.
 *
 * **Applied here rather than in `STEALTH_TUNING` or in `emitFootfall`**, and
 * `docs/beat-6-plan.md` rule 4 argued both alternatives down: `STEALTH_TUNING`
 * is an exported `as const` read directly by `emitSound()`, and adding a mutable
 * global to a deterministic fixed-step system is the wrong shape; growing
 * `emitFootfall` a multiplier would put weather in the stealth system's
 * vocabulary. The yard is what knows it is raining, so the yard's call site is
 * what scales the radius.
 *
 * 0.45 is a starting number and it is one of the two this beat most wants a
 * stopwatch on (the other is `STORM.tell`).
 */
const RAIN_MASKING = 0.45;

/**
 * Act 1 beat 6, *The Loading Yard*: the storm, Kestner, the bin, the flash and
 * the trailer.
 *
 * Until session 85 this was a branch in `main.ts` plus lines in nine other
 * places there (D-235). Where each piece now runs, against where it ran:
 *
 * - The director's step, the strike record, the carry, the two one-shot
 *   placements, the flags and her footfalls ran in one block right after the
 *   controller; they still do, whole and in order, in `afterMove` — including
 *   `watchedHim`, which reads the interaction system's completion BEFORE this
 *   tick's sampling, as it always has.
 * - The stealth step and capture ran after `physics.step`; `afterPhysics`.
 * - The flash's three sinks and the three rain loops were the host's render
 *   step; they are the part's.
 */
function createLoadingYardPartModule(capabilities: BeatPartCapabilities): BeatPartModule {
  const world = capabilities.world;
  if (world === undefined) throw new Error('beat 6 / part 1: the host offered no world capability');

  /** Written by the director every fixed step; read by the render step's mix. */
  let sheltered = false;
  const yard = new YardDirector(world.scene, {
    // The light and the sound are NOT simultaneous, and a player who notices
    // why is right: `Storm` schedules thunder at the strike plus an authored
    // distance. `near` is the storm overhead and it is every strike the player
    // can see; `far` is the rest of the weather, which they only ever hear.
    onThunder: (distance, at) => world.playOneShot(
      distance === 'near' ? 'thunder-near' : 'thunder-far',
      { gain: distance === 'near' ? 0.95 : 0.5, position: at },
    ),
    // Something heavy shifting in a steel box twenty metres away. The race is
    // empty and this is what says the truck is not.
    onRaceClang: (at) => world.playOneShot('race-clang', { gain: 0.55, position: at }),
    // The same lid that closed over her at the end of beat 5, opening. It is
    // the only sound in the game that is played twice for opposite reasons.
    onLid: (at) => world.playOneShot('bin-lid-close', { gain: 0.7, position: at, detune: 120 }),
    // **The whistle that called the dog OFF in beat 4**, and here it calls
    // somebody on. Kestner never runs, never grabs and never touches her; what
    // he does is signal, and the player already knows what that sound means.
    onWhistle: (at) => world.playOneShot('handler-whistle', { gain: 0.9, position: at }),
    onSheltered: (next) => { sheltered = next; },
  });

  // ── Rule 1's decorator, and it is the beat's one architectural decision ──
  //
  // The flash floods the yard and it is NOT a light. D-027 binds this hardware
  // by lights per fragment, an exterior has no walls to cull behind, and
  // `bakeLightGrid` is baked once at zone load precisely so that sampling is
  // O(1) during play — a flash implemented as a fixture would be a fifth light
  // AND would invalidate every baked value in the same stroke.
  //
  // **Anything reading `grid.values` directly bypasses this.** Nothing does,
  // and the gate that would catch it is `yard-beat.spec.ts`'s exposure delta,
  // which samples `exposureAt` at one fixed point between strikes and at peak
  // and asserts the difference — not this comment (D-097).
  const baked = bakeYardLightGrid();
  const grid: LightGrid = { ...baked, sample: (x, z) => Math.min(1, baked.sample(x, z) + yard.flash * FLASH_GAIN) };
  const cover = yardCover();
  // The watcher object itself, not a copy: `YardDirector.step` mutates this
  // exact object every fixed tick, and a copy would leave the stealth system
  // sampling the cone he had at construction while the visible man swept.
  const stealth = new StealthSystem(grid, cover, [yard.watcher], world.lineOfSight);
  const story = new BeatSequencer(LOADING_YARD_BEAT);
  const herFootfalls = new FootfallStride();

  const interactables: readonly Interactable[] = [
    // Him, under `observe`, from across the yard. **Fifteen metres of reach**,
    // which is by far the longest in the game, and it has to be: the plan's
    // third rule is that no camera in beat 6 comes closer than 4 m to his
    // face, and she watches him from behind the bin row twenty metres away.
    {
      id: YARD_IDS.kestner,
      at: { x: KESTNER_OBSERVE_AT.x, y: KESTNER_OBSERVE_AT.y, z: KESTNER_OBSERVE_AT.z },
      verbs: ['observe'],
      mapGlyph: 'person',
      reach: 15,
      // Long, like Otto's. `focus` is the reward for holding it (D-066) and
      // what she is holding it for is the count: the board, the pen, and the
      // fact that he does not look up.
      duration: 3.6,
      enabled: false,
    },
  ];

  /**
   * Sim time at every tell onset, and it is the determinism gate's whole seam.
   *
   * D-045: the simulation must replay. Two runs of the same seed have to put
   * the same strikes at the same times to the frame, and a poll would miss
   * most of a flash that rises and falls inside five fixed steps. The clock is
   * the part's own sum of the fixed `dt`s it was stepped with, which is the
   * host's paused-aware game clock: both start at boot and add the same `dt`
   * once per unpaused tick, this one after its read as the host's is.
   */
  const strikes: number[] = [];
  let simTime = 0;
  let tellWas = false;
  /** Latches for the two one-shot body moves the director asks for. */
  let climbedOut = false;
  let placedUnder = false;
  /** The rain bed, the harder layer under steel, and the diesel at the cab. */
  let rain: BeatPartLoop | null = null;
  let rainSteel: BeatPartLoop | null = null;
  let truck: BeatPartLoop | null = null;
  let disposed = false;

  const stopWeather = (fadeSeconds?: number): void => {
    rain?.stop(fadeSeconds);
    rainSteel?.stop(fadeSeconds);
    truck?.stop(fadeSeconds);
    rain = null;
    rainSteel = null;
    truck = null;
  };

  return {
    story,
    interactables,
    stealth,
    get fade(): number { return yard.fade; },
    // Beat 6 takes the lens twice — inside the bin and under the trailer — and
    // gives it back in between, which is the whole crossing (the legacy terms).
    get promptSuppressed(): boolean { return !disposed && (yard.cameraHeld || yard.fade > 0.5); },
    // Its shots always cut, and an eased version of the same shot is a different shot.
    framingCuts: true,
    framing: () => (disposed ? null : yard.framing()),
    afterMove({ dt, player, motion, raiseFlag, moveSelf }): void {
      // The director steps FIRST, because everything below reads a decision it
      // has just made: whether her body is still the bin's, whether it has just
      // been set down, and where the storm is in its envelope.
      yard.step(dt);
      // The tell's RISING edge, stamped with sim time.
      const telling = yard.stormState.tell;
      if (telling && !tellWas) strikes.push(simTime);
      tellWas = telling;
      simTime += dt;

      // Being in the bin, which is beat 5's carry for exactly the same reason:
      // the simulation stops being the thing that decides where she is.
      // `YARD_SPAWN` is INSIDE the bin row's collider, so this is not a nicety —
      // without it she spends stage 1 embedded in a solid.
      if (yard.carrying) moveSelf?.(yard.carryPose);
      // And the two one-shot moves, each latched here rather than in the
      // director: a getter that stays true forever would otherwise re-reset her
      // every tick and pin her to the spot.
      if (yard.outOfTheBin && !climbedOut) {
        climbedOut = true;
        moveSelf?.(yard.setDownAt);
        // Raised HERE and not when the climb-out cue fired: the flag says the
        // world has finished putting her on the apron (D-090).
        raiseFlag(YARD_FLAGS.outOfTheBin);
      }
      if (yard.placeUnderTrailer && !placedUnder) {
        placedUnder = true;
        moveSelf?.(yard.underTrailerAt);
      }

      // Two metres clear of the bin row, off `BIN_ROW`'s own z.
      if (player.z >= BIN_ROW_Z + 2) raiseFlag(YARD_FLAGS.clearOfBins);
      if (world.interaction.completed(YARD_IDS.kestner)) raiseFlag(YARD_FLAGS.watchedHim);
      if (yard.flashTaught) raiseFlag(YARD_FLAGS.flashTaught);
      // **`isUnderTrailer`, not the shadow region.** The region is the
      // axis-aligned bounds of a unit parked 0.35 rad off square (D-108), so
      // two of its corners are open yard.
      if (isUnderTrailer(player.x, player.z)) raiseFlag(YARD_FLAGS.underTheTrailer);

      // ── Her footfalls, and the storm is what makes them cheap ─────────────
      // Per STRIDE, beat 2's lesson; the surface is `wet`, and `RAIN_MASKING`
      // is the beat's own multiplier on top. `emitSound` rather than
      // `emitFootfall`, because the radius is the one difference this needs.
      if (motion === undefined) return;
      const heardAs = heardGait(motion.gait);
      if (herFootfalls.step(motion.speed * dt, heardAs, motion.grounded && !yard.carrying) && heardAs !== null) {
        stealth.emitSound({ position: { x: player.x, y: player.y, z: player.z }, radius: emissionRadius('wet', heardAs) * RAIN_MASKING });
      }
    },
    fixedStep(): void {
      // Everything this part decides on the fixed step is in `afterMove`,
      // where the legacy block ran, before interaction sampling.
    },
    afterPhysics({ dt, player, moveSelf }): void {
      // After `physics.step`, because the eye is a Rapier ray (see
      // `BeatPartWorld.lineOfSight`). What a detection costs is D-084's policy:
      // fade, back to the bins, the alarm left to `StealthSystem`, the
      // sequencer left alone.
      const snapshot = stealth.step(dt, { x: player.x, y: player.y, z: player.z });
      if (moveSelf !== undefined) yard.capture(snapshot, moveSelf);
    },
    handleCue(cue): void {
      yard.handle(cue as Parameters<YardDirector['handle']>[0]);
      // Never taken away again: she may look at him from anywhere for the rest
      // of the beat, including from under the trailer at the end of it.
      if (cue.id === YARD_CUES.offerKestner) world.interaction.setEnabled(YARD_IDS.kestner, true);
      // ── And the weather stops being played ────────────────────────────────
      // Faded rather than cut: `fadeOut` runs four seconds before this and the
      // rain is the last thing the player hears of the yard.
      if (cue.id === YARD_CUES.end) stopWeather(2.5);
    },
    renderStep(frameDt): void {
      if (disposed) return;
      // **This line was missing for the whole of beat 6's first build and the
      // beat was a black screen**: `update` interpolates the fade, and the
      // overlay exists at full black from the frame it is constructed. Every
      // assertion was green; it took a screenshot (D-048).
      yard.update(frameDt);
      // The strike, in two of its three places (D-107). `flash` is written once
      // per FIXED step by the director; these only read it, so the flash the
      // player sees and the flash the stealth grid used are the same number.
      // The rig's share is not decoration: raising the sky term makes surfaces
      // climb the toon ramp, so the yard's shapes come up out of the dark.
      world.stormFlash(yard.flash, 1 + yard.flash * FLASH_RIG_GAIN, 1 + yard.flash * FLASH_SKY_GAIN);
      // The rain's second layer, cross-faded on whether there is steel over
      // her: what makes the bin and the trailer feel like shelter.
      //
      // **Acquired here and re-acquired if inactive**: a loop requested before
      // the click that unlocks the audio context comes back permanently inert,
      // and three loops started at construction were three loops that never
      // made a sound (found by independent review).
      if (!yard.ended) {
        if (rain === null || !rain.active) rain = world.playLoop('rain-bed', { gain: 0 });
        if (rainSteel === null || !rainSteel.active) rainSteel = world.playLoop('rain-on-steel', { gain: 0 });
        if (truck === null || !truck.active) {
          truck = world.playLoop('truck-idle', { gain: 0, position: { x: TRUCK_CAB_AT.x, y: 1.2, z: TRUCK_CAB_AT.z } });
        }
        truck.setGain(0.35, 0.5);
      }
      rainSteel?.setGain(sheltered ? 0.55 : 0, 0.35);
      rain?.setGain(sheltered ? 0.22 : 0.5, 0.35);
    },
    diagnostics() {
      return {
        kind: 'yard',
        yard: { ...yard.debug, fade: yard.fade, sheltered, strikes: [...strikes] },
        // A query over the SAME decorated grid and cover the stealth system
        // holds: a gate that re-baked its own would prove a decorator can
        // work, not that the game uses one. y is her standing height, so a
        // cover volume with a lid does not read as concealing the floor.
        exposureAt: (x: number, z: number) => exposureAt({ x, y: 0.16, z }, grid, cover),
      } satisfies { kind: 'yard'; yard: YardDebug; exposureAt: unknown };
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      stopWeather();
      yard.dispose();
    },
  };
}

export const LOADING_YARD_PART_MODULE: BeatPartModuleDefinition = {
  beat: 6,
  part: 1,
  room: 'yard',
  create: createLoadingYardPartModule,
};
